import React, { useEffect, useState, useCallback } from "react";
import { Col, Row, Result } from "antd";
// import { Button } from "@digitallabs/one-x-ui";
import { Button } from "../../resourses/digitallabs/one-x-ui";
import { useLoader } from "../../context/LoaderContext";
import { getQuestionSet } from "../../services/serviceCollections/dmqService";

import StepPersonalDetails from "../steps/StepPersonalDetails";
import StepMedicalHistory from "../steps/StepMedicalHistory";
import StepConfirmDetails from "../steps/StepConfirmDetails";
import StepNRIQuestonnaire from "../steps/StepNRIQuestonnaire";
import StepDiabeticQuestionnaire from "../steps/StepDiabeticQuestionnaire";
import StepBloodPressureQuestionnaire from "../steps/StepBloodPressureQuestionnaire";
import StepKidneyQuestionnaire from "../steps/StepKidneyQuestionnaire";

import CustomStepper, { isStepCompleted } from "../../components/common/CustomStepper";
import {
  getCurrentStep,
  getQuestionBank,
  getStoredFormData,
  setQuestionBank,
  saveCurrentStep,
  saveQuestionSetId,
  getUserNRIDetails,
  getCurrentQuestionSetId,
  getDMQMedicalConditions,
} from "../../utils/formStorage";
import { buildStepFlow } from "../../utils/stepFlowManager";
import "../../styles/DMQFormFilling.css";

import { showCatchError, showMessage } from "../../utils/toastMessageUtil";
import { DMQStepNames, MAIN_STEPS } from "../../constants/constants";
import { withAuth } from "../../utils/withAuth";
import StepThyroidQuestionnaire from "../steps/StepThyroidQuestionnaire";
import StepForeignNationalQuestionnaire from "../steps/StepForeignNationalQuestionnaire";

const FormFilling = () => {
  const { setLoading } = useLoader();

  const [currentStep, setCurrentStep] = useState(getCurrentStep() || MAIN_STEPS.PERSONAL_DETAILS);

  const [errorPage, setErrorPage] = useState(false);
  const [retryTimer, setRetryTimer] = useState(30);
  const [stepFlow, setStepFlow] = useState([]);

  const stored = getStoredFormData();
  const medicalHistoryDetails = stored?.[DMQStepNames.MEDICAL_HISTORY] ?? {};
  const storedConditions = getDMQMedicalConditions();

  const isUserNRI = !!storedConditions?.isUserNRI;
  const isUserFN = !!storedConditions?.isUserFN;
  const isUserDiabetic = medicalHistoryDetails?.diabetes === "Yes";
  const isUserBloodPresure = medicalHistoryDetails?.highBP === "Yes";
  const isUserThyroid = medicalHistoryDetails?.thyroidEndocrine === "Yes";
  const isUserKidney = medicalHistoryDetails?.kidneyDisorder === "Yes";

  useEffect(() => {
    const handleStepUpdate = (e) => {
      const newStep = e.detail?.step;
      if (newStep) {
        setCurrentStep(newStep);
      }
    };

    window.addEventListener("dmq-step-change", handleStepUpdate);

    return () => {
      window.removeEventListener("dmq-step-change", handleStepUpdate);
    };
  }, []);

  useEffect(() => {
    const handleInternalStepUpdate = (e) => {
      // Not used now, but keeps system consistent
      console.log("Internal step changed:", e.detail?.internalStep);
    };

    window.addEventListener("step-change-event", handleInternalStepUpdate);

    return () => {
      window.removeEventListener("step-change-event", handleInternalStepUpdate);
    };
  }, []);

  // Build step flow
  useEffect(() => {
    const steps = buildStepFlow(isUserNRI, isUserFN, isUserDiabetic, isUserBloodPresure, isUserThyroid, isUserKidney);
    setStepFlow(steps);
  }, [isUserNRI, isUserFN, isUserDiabetic, isUserBloodPresure, isUserThyroid, isUserKidney]);

  /**
   * FETCH QUESTION BANK
   */
  const populateQuestionSet = useCallback(async () => {
    try {
      setLoading(true);
      setErrorPage(false);

      const result = await getQuestionSet();

      if (result.message === "OK") {
        const set = result?.data?.content;
        const questionSetId = result?.data?._id;
        saveQuestionSetId(questionSetId);
        setQuestionBank(set);
      } else {
        throw new Error("Invalid response");
      }

      setTimeout(() => setLoading(false), 1000);
    } catch (error) {
      console.log("Error on get question set", error);
      showCatchError(error.message || error);

      setErrorPage(true);
      setRetryTimer(30);
      setLoading(false);
    }
  }, [setLoading]);

  /**
   * INITIAL LOAD
   */
  useEffect(() => {
    const questionBank = getQuestionBank();
    const questionSetId = getCurrentQuestionSetId();

    if (!questionBank || !questionSetId) {
      setErrorPage(true);
      populateQuestionSet();
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [populateQuestionSet]);
  /**
   * RETRY TIMER
   */
  useEffect(() => {
    let interval;

    if (errorPage && retryTimer > 0) {
      interval = setInterval(() => {
        setRetryTimer((prev) => prev - 1);
      }, 1000);
    } else if (errorPage && retryTimer === 0) {
      populateQuestionSet();
    }

    return () => clearInterval(interval);
  }, [errorPage, retryTimer, populateQuestionSet]);

  /**
   * STEP NAVIGATION
   */
  const handleStepChange = (stepId) => {
    setCurrentStep(stepId);
    saveCurrentStep(stepId);
  };

  /**
   * STEP VALIDATION
   */

  const checkForStepChange = (targetStepId) => {
    const stored = getStoredFormData();

    const steps = stepFlow;

    const currentIndex = steps.findIndex((s) => s.id === currentStep);
    const targetIndex = steps.findIndex((s) => s.id === targetStepId);

    // Allow backward
    if (targetIndex <= currentIndex) {
      handleStepChange(targetStepId);
      return;
    }

    // Find FIRST incomplete step before target
    const incompleteStep = steps.slice(0, targetIndex).find((step) => !isStepCompleted(step.id, stored));

    if (incompleteStep) {
      showMessage("warning", `Please complete "${incompleteStep.title}" before moving forward.`);
      return;
    }

    handleStepChange(targetStepId);
  };

  /**
   * STEP COMPONENT RENDER
   */
  const getStepComponent = () => {
    switch (currentStep) {
      case MAIN_STEPS.PERSONAL_DETAILS:
        return <StepPersonalDetails handleStepChange={handleStepChange} />;

      case MAIN_STEPS.MEDICAL_HISTORY:
        return <StepMedicalHistory handleStepChange={handleStepChange} />;

      case MAIN_STEPS.NRI_QUESTIONNAIRE:
        return <StepNRIQuestonnaire handleStepChange={handleStepChange} />;

      case MAIN_STEPS.FOREIGN_NATIONAL_QUESTIONNAIRE:
        return <StepForeignNationalQuestionnaire handleStepChange={handleStepChange} />;

      case MAIN_STEPS.DIABETIC_QUESTIONNAIRE:
        return <StepDiabeticQuestionnaire handleStepChange={handleStepChange} />;

      case MAIN_STEPS.BLOOD_PRESSURE_QUESTIONNAIRE:
        return <StepBloodPressureQuestionnaire handleStepChange={handleStepChange} />;

      case MAIN_STEPS.THYROID_QUESTIONNAIRE:
        return <StepThyroidQuestionnaire handleStepChange={handleStepChange} />;

      case MAIN_STEPS.KIDNEY_QUESTIONNAIRE:
        return <StepKidneyQuestionnaire handleStepChange={handleStepChange} />;

      case MAIN_STEPS.CONFIRM_DETAILS:
        return <StepConfirmDetails handleStepChange={handleStepChange} />;

      default:
        return null;
    }
  };

  /**
   * ERROR PAGE
   */
  if (errorPage) {
    return (
      <div style={{ padding: "50px 20px" }}>
        <Result
          status="500"
          title="Oops!"
          subTitle={`Retrying automatically in ${retryTimer} seconds.`}
          extra={[
            <Button variant="primary" onClick={populateQuestionSet} key="retry">
              Retry Now
            </Button>,
          ]}
        />
      </div>
    );
  }

  return (
    <div>
      <CustomStepper steps={stepFlow} currentStep={currentStep} onStepChange={checkForStepChange} />

      <div style={{ padding: 10 }} className="form-container">
        <Row gutter={16}>
          <Col span={24}>{getStepComponent()}</Col>
        </Row>
      </div>
    </div>
  );
};

export default withAuth(FormFilling);
